import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './About.css';
import rocketIcon from '../../assets/images/icons/rocket.gif';
import shieldIcon from '../../assets/images/icons/shield.gif';
import laptopIcon from '../../assets/images/icons/laptop.gif';

// Custom hook for animated counter
const useAnimatedCounter = (end: number, duration: number = 2000) => {
  const [count, setCount] = useState(0);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    if (isInView) {
      let startTime: number;
      let animationFrame: number;

      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentCount = Math.floor(easeOutQuart * end);
        
        setCount(currentCount);
        
        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        }
      };

      animationFrame = requestAnimationFrame(animate);
      
      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }
  }, [end, duration, isInView]);

  return { count, setIsInView };
};

const About: React.FC = () => {
  const projectsCounter = useAnimatedCounter(50);
  const yearsCounter = useAnimatedCounter(9);
  const satisfactionCounter = useAnimatedCounter(100);

  return (
    <section className="section about-section" id="about">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="about-content"
          onViewportEnter={() => {
            projectsCounter.setIsInView(true);
            yearsCounter.setIsInView(true);
            satisfactionCounter.setIsInView(true);
          }}
        >
          <h2 className="section-title">Who We Are</h2>
          <div className="about-grid">
            <div className="about-text">
              <p className="about-description">
                Xenvota Tech Solutions is a team of experienced mobile and full-stack engineers who build secure, maintainable, and scalable digital products. We specialize in cross-platform apps, backend services, and full-cycle delivery with proven results in enterprise-grade projects.
              </p>
              <div className="about-stats">
                <motion.div 
                  className="stat-item"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <h3>{projectsCounter.count}+</h3>
                  <p>Projects Delivered</p>
                </motion.div>
                <motion.div 
                  className="stat-item"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <h3>{yearsCounter.count}+</h3>
                  <p>Years Experience</p>
                </motion.div>
                <motion.div 
                  className="stat-item"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  viewport={{ once: true }}
                >
                  <h3>{satisfactionCounter.count}%</h3>
                  <p>Client Satisfaction</p>
                </motion.div>
              </div>
            </div>
            <div className="about-visual">
              <div className="about-cards">
                <div className="about-card">
                  <div className="card-icon">
                    <img src={rocketIcon} alt="Fast Delivery" />
                  </div>
                  <h4>Fast Delivery</h4>
                  <p>Quick turnaround times without compromising quality</p>
                </div>
                <div className="about-card">
                  <div className="card-icon">
                    <img src={shieldIcon} alt="Secure Code" />
                  </div>
                  <h4>Secure Code</h4>
                  <p>Enterprise-grade security practices in every project</p>
                </div>
                <div className="about-card">
                  <div className="card-icon">
                    <img src={laptopIcon} alt="Scalable Solutions" />
                  </div>
                  <h4>Scalable Solutions</h4>
                  <p>Built to grow with your business needs</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About; 